// /support/my — список тикетов залогиненного юзера.
// Фильтры-чипы: Все / Открытые (open+waiting_mod+waiting_user) / Закрытые.
// Гость перенаправляется на /login.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SupportShell from "../components/support/SupportShell";
import { useAuth } from "../context/AuthContext";
import { getMyTickets } from "../api/tickets";
import TicketStatusBadge from "../components/support/TicketStatusBadge";
import WebPushBanner from "../components/support/WebPushBanner";
import { ChatBubbleLeftRightIcon } from "../components/icons/ChatBubbleLeftRightIcon";
import { Button, EmptyState, Skeleton, fmtNum } from "../components/ui";
import usePageTitle from "../hooks/usePageTitle";
import "../styles/support.css";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (diff < 60_000) return "только что";
    if (diff < 3_600_000) return Math.floor(diff / 60_000) + " мин назад";
    if (diff < 86_400_000) return Math.floor(diff / 3_600_000) + " ч назад";
    if (diff < 604_800_000) return Math.floor(diff / 86_400_000) + " дн назад";
    return new Date(iso).toLocaleDateString("ru-RU");
  } catch {
    return "";
  }
}

const OPEN_STATUSES = new Set(["open", "waiting_mod", "waiting_user"]);

export default function SupportListPage() {
  usePageTitle("Мои обращения");
  const { isAuthenticated, initializing } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("open"); // "all" | "open" | "closed"
  const [tick, setTick] = useState(0);   // триггер для кнопки «Повторить»

  useEffect(() => {
    if (initializing) return;
    if (!isAuthenticated) {
      navigate("/login?next=/support/my", { replace: true });
      return;
    }
    let cancelled = false;
    setErr(null);
    getMyTickets()
      .then((d) => { if (!cancelled) setItems(d.items || []); })
      .catch((e) => { if (!cancelled) setErr(e.message); });
    return () => { cancelled = true; };
  }, [initializing, isAuthenticated, navigate, tick]);

  if (initializing || !isAuthenticated) {
    return (
      <SupportShell>
        <div className="sup-page">
          <div className="sup-msg-info">Загрузка…</div>
        </div>
      </SupportShell>
    );
  }

  const counts = (items || []).reduce(
    (acc, t) => {
      acc.all += 1;
      if (OPEN_STATUSES.has(t.status)) acc.open += 1;
      else acc.closed += 1;
      return acc;
    },
    { all: 0, open: 0, closed: 0 }
  );

  const visible = (items || []).filter((t) => {
    if (filter === "all") return true;
    const isOpen = OPEN_STATUSES.has(t.status);
    return filter === "open" ? isOpen : !isOpen;
  });

  return (
    <SupportShell>
      <div className="sup-page">
        <div className="sup-page__hero">
          <h1>Мои обращения</h1>
          <p>Здесь видна вся переписка с МИНом по твоим обращениям.</p>
        </div>

        <div className="sup-page__row">
          <button type="button" className="sup-btn-primary"
                  onClick={() => navigate("/support")}>
            <ChatBubbleLeftRightIcon size={16} color="#fff" />
            Новое обращение
          </button>
        </div>

        <WebPushBanner />

        <div className="sup-list-filters">
          <button
            type="button"
            className={`sup-list-filter ${filter === "open" ? "is-active" : ""}`}
            onClick={() => setFilter("open")}
          >
            Открытые
            <span className="sup-list-filter__count ui-num">{fmtNum(counts.open)}</span>
          </button>
          <button
            type="button"
            className={`sup-list-filter ${filter === "closed" ? "is-active" : ""}`}
            onClick={() => setFilter("closed")}
          >
            Закрытые
            <span className="sup-list-filter__count ui-num">{fmtNum(counts.closed)}</span>
          </button>
          <button
            type="button"
            className={`sup-list-filter ${filter === "all" ? "is-active" : ""}`}
            onClick={() => setFilter("all")}
          >
            Все
            <span className="sup-list-filter__count ui-num">{fmtNum(counts.all)}</span>
          </button>
        </div>

        {/* Ошибка с выходом: раньше строка текста без действия, и повторить
            запрос можно было только перезагрузкой всей страницы. */}
        {err && (
          <EmptyState
            icon={<ChatBubbleLeftRightIcon size={40} />}
            title="Не получилось загрузить обращения"
            desc="Проверь соединение и попробуй ещё раз."
            action={
              <Button variant="primary" onClick={() => { setItems(null); setTick((t) => t + 1); }}>
                Повторить
              </Button>
            }
          />
        )}

        {/* Скелетон вместо текста «Загрузка…»: форма списка известна,
            и после загрузки ничего не прыгает. */}
        {!items && !err && (
          <div className="sup-list-skeleton">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} h={72} r="var(--r-md)" />
            ))}
          </div>
        )}

        {items && !err && visible.length === 0 && (
          <EmptyState
            icon={<ChatBubbleLeftRightIcon size={40} />}
            title={
              filter === "open"
                ? "Открытых обращений нет"
                : filter === "closed"
                ? "Закрытых обращений ещё нет"
                : "Обращений пока нет"
            }
            desc={
              filter === "all"
                ? "Если что-то не работает или есть вопрос — напиши, разберёмся."
                : "Переключись на другой фильтр или создай новое обращение."
            }
            action={
              <Button variant="primary" onClick={() => navigate("/support")}>
                Новое обращение
              </Button>
            }
          />
        )}

        {visible.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`sup-ticket-card ${t.unread_for_user ? "is-unread" : ""}`}
            onClick={() => navigate(`/support/ticket/${t.id}`)}
          >
            <div className="sup-ticket-card__body">
              <div className="sup-ticket-card__subject">
                #{t.id} · {t.subject}
              </div>
              <div className="sup-ticket-card__meta">
                <TicketStatusBadge status={t.status} />
                <span>{timeAgo(t.last_msg_at)}</span>
                {t.last_msg_by === "mod" && (
                  <span className="sup-ticket-card__lastby">
                    Последний ответ — МИН
                  </span>
                )}
              </div>
            </div>
            <span className="sup-ticket-card__time">
              {t.unread_for_user && (
                <span className="sup-ticket-card__unread-dot"
                      title="Непрочитанное" />
              )}
            </span>
          </button>
        ))}
      </div>
    </SupportShell>
  );
}
